import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProfileContext } from '../hooks/useProfileContext';
import {
  apiAiChatAction,
  apiAiChatApply,
  apiAiCredits,
  ApiError,
  ProposedAction,
  type AiCreditsInfo,
} from '../api';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';

interface ChatEntry {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  error?: boolean;
  /** A confirmed-awaiting AI proposal. When present, the entry renders as a
   * proposal card (summary + Apply/Cancel) instead of a plain bubble. */
  proposal?: ProposedAction;
  applied?: boolean;
  cancelled?: boolean;
  applying?: boolean;
  applyError?: boolean;
}

let nextId = 0;
const nextEntryId = () => `m${Date.now()}_${nextId++}`;

// Render an update_show / add_roadmap_item change legibly inside the card.
function renderChanges(changes: Record<string, unknown>): string {
  if (!changes || typeof changes !== 'object') return '';
  return Object.entries(changes)
    .filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== '')
    .map(([k, v]) => `${k}: ${String(v)}`)
    .join(' · ');
}

export default function AiAssistantPage() {
  const { t } = useTranslation();
  const { activeProfileId, getProfile } = useProfileContext();

  const band = activeProfileId ? getProfile(activeProfileId) : null;
  const bandName = band?.name || '';

  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // 2-pool credit info (monthly allowance + persistent top-up) + plan.
  const [creditInfo, setCreditInfo] = useState<AiCreditsInfo | null>(null);
  const [outOfCredits, setOutOfCredits] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Load the current credit pools + consumed total on mount and when the
  // active band changes.
  useEffect(() => {
    apiAiCredits()
      .then((d) => setCreditInfo(d))
      .catch(() => setCreditInfo(null));
  }, [activeProfileId]);

  // Keep the chat pinned to the bottom.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [entries, sending]);

  const patchEntry = useCallback((id: string, patch: Partial<ChatEntry>) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  }, []);

  const applyProposal = useCallback(
    async (entryId: string, proposal: ProposedAction) => {
      if (!activeProfileId || !proposal.action) return;
      patchEntry(entryId, { applying: true, applyError: false });
      try {
        await apiAiChatApply({ bandId: activeProfileId, action: proposal });
        patchEntry(entryId, { applying: false, applied: true });
      } catch (e) {
        const isOut = e instanceof ApiError && e.status === 402;
        if (isOut) setOutOfCredits(true);
        patchEntry(entryId, { applying: false, applyError: true });
      }
    },
    [activeProfileId, patchEntry],
  );

  const cancelProposal = useCallback(
    (entryId: string) => patchEntry(entryId, { cancelled: true }),
    [patchEntry],
  );

  const send = useCallback(async () => {
    const message = input.trim();
    if (!message || sending || !activeProfileId) return;
    setInput('');
    setOutOfCredits(false);
    const userEntry: ChatEntry = { id: nextEntryId(), role: 'user', content: message };
    setEntries((prev) => [...prev, userEntry]);
    setSending(true);
    try {
      const res = await apiAiChatAction({ bandId: activeProfileId, message });
      if (res.action) {
        // An actionable proposal -> render the confirm card.
        setEntries((prev) => [
          ...prev,
          { id: nextEntryId(), role: 'assistant', content: res.summary, proposal: res },
        ]);
      } else {
        // A plain question -> read-only answer (nothing is written).
        setEntries((prev) => [
          ...prev,
          { id: nextEntryId(), role: 'assistant', content: res.summary || '' },
        ]);
      }
      // Refresh the two pools after a metered call (allowance/top-up split).
      apiAiCredits().then((d) => setCreditInfo(d)).catch(() => {});
    } catch (e) {
      if (e instanceof ApiError && e.status === 402) {
        setOutOfCredits(true);
        setCreditInfo((prev) => (prev ? { ...prev, total: 0, allowance: 0, topup: 0 } : prev));
      } else {
        const msg = t('aiAssistant.requestFailed');
        setEntries((prev) => [
          ...prev,
          { id: nextEntryId(), role: 'assistant', content: msg, error: true },
        ]);
      }
    } finally {
      setSending(false);
    }
  }, [input, sending, activeProfileId, t]);

  return (
    <div className="page ai-assistant-page">
      <PageHeader
        title={withPageIcon('aiAssistant.title', t('aiAssistant.title'))}
        actions={
          <>
            <span className="ai-assistant-band">
              {bandName ? t('aiAssistant.bandFor', { band: bandName }) : t('aiAssistant.noBand')}
            </span>
            <span
              className={`ai-credit-badge ${creditInfo && creditInfo.total === 0 && !creditInfo.unlimited ? 'ai-credit-empty' : ''}`}
            >
              {creditInfo === null ? (
                t('aiAssistant.creditsLoading')
              ) : creditInfo.unlimited ? (
                t('aiAssistant.creditsUnlimited', { used: creditInfo.consumed })
              ) : (
                <>
                  <span className="ai-credit-pool-chip">
                    {t('aiAssistant.allowance', { n: creditInfo.allowance })}
                  </span>
                  <span className="ai-credit-pool-chip">
                    {t('aiAssistant.topup', { n: creditInfo.topup })}
                  </span>
                </>
              )}
            </span>
          </>
        }
      />

      <p className="ai-assistant-subtitle">{t('aiAssistant.subtitle')}</p>

      <div className="ai-chat">
        <div className="ai-chat-scroll" ref={scrollRef}>
          {entries.length === 0 && !sending && (
            <div className="ai-chat-empty">{t('aiAssistant.empty')}</div>
          )}

          {entries.map((e) => (
            <div
              key={e.id}
              className={`ai-msg ai-msg-${e.role}${e.error ? ' ai-msg-error' : ''}`}
            >
              {e.proposal && !e.cancelled && e.proposal.action ? (
                <div className="ai-msg-bubble ai-action-card">
                  <div className="ai-action-card-head">
                    <span className="ai-action-badge">{e.proposal.action}</span>
                    <span className="ai-action-summary">{e.content}</span>
                  </div>
                  <div className="ai-action-changes">{renderChanges(e.proposal.changes || {})}</div>
                  {e.applied ? (
                    <div className="ai-action-status ai-action-ok">
                      ✓ {t('aiAssistant.applied')}
                    </div>
                  ) : e.applyError ? (
                    <div className="ai-action-status ai-action-error">
                      {t('aiAssistant.applyFailed')}
                    </div>
                  ) : (
                    <div className="ai-action-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={e.applying}
                        onClick={() => void applyProposal(e.id, e.proposal!)}
                      >
                        {e.applying ? t('aiAssistant.applying') : t('aiAssistant.apply')}
                      </button>
                      <button
                        type="button"
                        className="btn"
                        disabled={e.applying}
                        onClick={() => cancelProposal(e.id)}
                      >
                        {t('aiAssistant.cancel')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="ai-msg-bubble">{e.content}</div>
              )}
            </div>
          ))}

          {sending && (
            <div className="ai-msg ai-msg-assistant">
              <div className="ai-msg-bubble ai-msg-thinking">{t('aiAssistant.thinking')}</div>
            </div>
          )}

          {outOfCredits && (
            <div className="ai-msg ai-msg-assistant">
              <div className="ai-msg-bubble ai-msg-error">{t('aiAssistant.outOfCredits')}</div>
            </div>
          )}
        </div>

        <form
          className="ai-chat-input"
          onSubmit={(ev) => {
            ev.preventDefault();
            void send();
          }}
        >
          <input
            className="form-input"
            value={input}
            onChange={(ev) => setInput(ev.target.value)}
            placeholder={t('aiAssistant.placeholder')}
            disabled={sending || !activeProfileId}
            aria-label={t('aiAssistant.placeholder')}
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={sending || !input.trim() || !activeProfileId}
          >
            {sending ? t('aiAssistant.sending') : t('aiAssistant.send')}
          </button>
        </form>
      </div>
    </div>
  );
}
